
export interface PayrollResult {
    gross: number;
    tax: number;
    ni: number;
    employerNi: number;
    pension: number;
    sspAmount?: number; // Statutory Sick Pay
    net: number;
    taxWeek?: number;
    taxMethod: 'Cumulative' | 'Week 1/Month 1';
}

export interface CumulativeData {
    ytdGross: number;
    ytdTax: number;
    weekNumber: number; // 1-52
}

/**
 * Determines the UK Tax Week number based on a given date.
 * Tax year starts April 6th.
 */
export const getUkTaxWeek = (date: Date): number => {
    if (isNaN(date.getTime())) return 1;

    const year = date.getFullYear();
    // Tax year start for the given date's calendar year
    let taxYearStart = new Date(year, 3, 6); // April 6th

    // If date is before April 6th, it belongs to the previous tax year
    if (date < taxYearStart) {
        taxYearStart = new Date(year - 1, 3, 6);
    }

    const diffTime = Math.abs(date.getTime() - taxYearStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Week 1 is days 0-6, Week 2 is 7-13, etc.
    const week = Math.ceil(diffDays / 7);
    return isFinite(week) ? (week > 52 ? 52 : Math.max(1, week)) : 1;
};

export interface TaxRates {
    ANNUAL_BASIC_LIMIT: number;
    ANNUAL_HIGHER_LIMIT: number;
    ANNUAL_NI_PRIMARY: number;
    ANNUAL_NI_SECONDARY: number;
    ANNUAL_NI_UPPER: number;
    ANNUAL_PENSION_TRIGGER: number;
    ANNUAL_PENSION_LOWER: number;
    ANNUAL_PENSION_UPPER: number;
}

export const TAX_RATES_BY_YEAR: Record<string, TaxRates> = {
    "2023_24": {
        ANNUAL_BASIC_LIMIT: 37700,
        ANNUAL_HIGHER_LIMIT: 125140,
        ANNUAL_NI_PRIMARY: 12570,
        ANNUAL_NI_SECONDARY: 9100,
        ANNUAL_NI_UPPER: 50270,
        ANNUAL_PENSION_TRIGGER: 10000,
        ANNUAL_PENSION_LOWER: 6240,
        ANNUAL_PENSION_UPPER: 50270,
    },
    "2024_25": { // 2024/25 Rates
        ANNUAL_BASIC_LIMIT: 37700,
        ANNUAL_HIGHER_LIMIT: 125140,
        ANNUAL_NI_PRIMARY: 12570,
        ANNUAL_NI_SECONDARY: 9100,
        ANNUAL_NI_UPPER: 50270,
        ANNUAL_PENSION_TRIGGER: 10000,
        ANNUAL_PENSION_LOWER: 6240,
        ANNUAL_PENSION_UPPER: 50270,
    },
    "2025_26": { // Projected 2025/26 Rates (Subject to change)
        ANNUAL_BASIC_LIMIT: 37700,
        ANNUAL_HIGHER_LIMIT: 125140,
        ANNUAL_NI_PRIMARY: 12570,
        ANNUAL_NI_SECONDARY: 9100,
        ANNUAL_NI_UPPER: 50270,
        ANNUAL_PENSION_TRIGGER: 10000,
        ANNUAL_PENSION_LOWER: 6240,
        ANNUAL_PENSION_UPPER: 50270,
    },
    "2026_27": { // 2026/27 Compliance (Strategy)
        ANNUAL_BASIC_LIMIT: 37700,
        ANNUAL_HIGHER_LIMIT: 125140,
        ANNUAL_NI_PRIMARY: 12570,
        ANNUAL_NI_SECONDARY: 9100,
        ANNUAL_NI_UPPER: 50270,
        ANNUAL_PENSION_TRIGGER: 10000,
        ANNUAL_PENSION_LOWER: 6240,
        ANNUAL_PENSION_UPPER: 50270,
    }
};

export const DEFAULT_SSP_DAILY_RATE = 24.65; // £123.25 / 5 days (2026 Rate)
export const SSP_WEEKLY_RATE_2026 = 123.25;

export const getTaxRatesForDate = (date: Date): TaxRates => {
    if (isNaN(date.getTime())) return TAX_RATES_BY_YEAR["2024_25"];

    const year = date.getFullYear();
    const month = date.getMonth(); // 0-indexed, so 3 is April
    const day = date.getDate();

    let taxYearStartYear = year;
    if (month < 3 || (month === 3 && day < 6)) {
        taxYearStartYear = year - 1;
    }

    const key = `${taxYearStartYear}_${(taxYearStartYear + 1).toString().slice(-2)}`;
    return TAX_RATES_BY_YEAR[key] || TAX_RATES_BY_YEAR["2024_25"]; // Fallback to 2024_25
};

/**
 * Calculates UK PAYE Deductions
 * Supports both W1/M1 (Standard) and Cumulative (10/10 Accuracy) modes.
 */
export const calculatePayroll = (
    grossPay: number,
    taxCode: string = '1257L',
    frequency: 'weekly' | 'monthly' = 'weekly',
    cumulativeData?: CumulativeData,
    payrollDate: Date = new Date(),
    sickDays: number = 0,
    sspDailyRate: number = DEFAULT_SSP_DAILY_RATE,
    use2026SspRules: boolean = false
): PayrollResult => {
    // --- Constants (Dynamic based on Date) ---
    const rates = getTaxRatesForDate(payrollDate);

    // --- 1. Determine Period Scaling ---
    let scalingFactor = 1;
    let periodGross = grossPay;
    let taxPaidYTD = 0;
    
    // --- 0. Sickness Pay (2026 Compliance) ---
    // Traditional SSP has 3 "waiting days". 2026 rules (Day-One Pay) remove them.
    const effectiveSickDays = use2026SspRules ? sickDays : Math.max(0, sickDays - 3);
    
    // 2026 Rule: If employee earns < £123.25/week, they get 80% of average earnings instead of the flat rate.
    // We estimate 'weekly earnings' from the grossPay if not provided, assuming grossPay is for the frequency period.
    let sspAmount = effectiveSickDays * sspDailyRate;
    
    if (use2026SspRules && effectiveSickDays > 0) {
        const weeklyEarningsEstimate = frequency === 'weekly' ? grossPay : (grossPay * 12 / 52);
        const weeklySSPCap = Math.min(SSP_WEEKLY_RATE_2026, weeklyEarningsEstimate * 0.8);
        
        // Convert weekly cap back to daily cap based on the provided daily rate's implied qualifying days
        // impliedDays = 123.25 / sspDailyRate
        // dailySSPCap = weeklySSPCap / impliedDays = (weeklySSPCap * sspDailyRate) / 123.25
        const dailySSPCap = (weeklySSPCap * sspDailyRate) / SSP_WEEKLY_RATE_2026;
        
        sspAmount = effectiveSickDays * dailySSPCap;
    }
    
    // Total gross is normal hours pay + sickness pay
    const totalGrossForPeriod = grossPay + sspAmount;
    periodGross = totalGrossForPeriod;
 
    let taxMethod: 'Cumulative' | 'Week 1/Month 1' = 'Week 1/Month 1';

    if (cumulativeData) {
        // CUMULATIVE MODE (10/10 Accuracy)
        // We calculate tax on the TOTAL earnings for the year so far, then subtract what was already paid.
        taxMethod = 'Cumulative';
        if (frequency === 'weekly') {
            scalingFactor = cumulativeData.weekNumber / 52;
        } else {
            scalingFactor = cumulativeData.weekNumber / 12; // Assuming weekNumber is actually Month Number 1-12 here
        }
        periodGross = cumulativeData.ytdGross + grossPay;
        taxPaidYTD = cumulativeData.ytdTax;
    } else {
        // W1/M1 MODE (Standard Estimate)
        // We treat this pay period as an isolated slice of the year.
        const divisor = frequency === 'weekly' ? 52 : 12;
        scalingFactor = 1 / divisor;
        // periodGross is just this week's pay
    }

    // --- 2. Parse Tax Code ---
    const code = taxCode.toUpperCase().trim();

    let annualAllowance = 0;
    let additionalTaxablePay = 0; // For K codes
    let taxBasis: 'STANDARD' | 'BR' | 'D0' | 'D1' | 'NT' = 'STANDARD';

    const numberPart = parseInt(code.replace(/[^\d]/g, ''), 10);
    const validNumber = !isNaN(numberPart) ? numberPart : 0;

    if (code === 'NT') taxBasis = 'NT';
    else if (code === 'BR') taxBasis = 'BR';
    else if (code === 'D0') taxBasis = 'D0';
    else if (code === 'D1') taxBasis = 'D1';
    else if (code === '0T') {
        annualAllowance = 0;
        taxBasis = 'STANDARD';
    } else if (code.startsWith('K')) {
        // K codes: Negative Allowance (Add to taxable pay)
        annualAllowance = 0;
        additionalTaxablePay = (validNumber * 10);
        taxBasis = 'STANDARD';
    } else {
        // Standard Codes (1257L)
        // L, M, N suffixes typically imply standard allowance logic (M/N transfer allowance is handled via code number adjustment by HMRC before issuing code)
        annualAllowance = (validNumber * 10);
    }

    // Scale Allowance/Additions to the Period (Cumulative or Single)
    const periodAllowance = annualAllowance * scalingFactor;
    const periodAddPay = additionalTaxablePay * scalingFactor;

    // --- 3. Calculate Income Tax ---
    let totalTaxDue = 0;

    // Taxable Pay for Calculation
    const taxablePay = Math.max(0, periodGross - periodAllowance) + periodAddPay;

    if (taxBasis === 'NT') {
        totalTaxDue = 0;
    } else if (taxBasis === 'BR') {
        totalTaxDue = taxablePay * 0.20;
    } else if (taxBasis === 'D0') {
        totalTaxDue = taxablePay * 0.40;
    } else if (taxBasis === 'D1') {
        totalTaxDue = taxablePay * 0.45;
    } else {
        // Progressive Bands (Scaled)
        const bandBasic = rates.ANNUAL_BASIC_LIMIT * scalingFactor;
        const bandHigher = rates.ANNUAL_HIGHER_LIMIT * scalingFactor;

        if (taxablePay > bandHigher) {
            totalTaxDue += (taxablePay - bandHigher) * 0.45;
            totalTaxDue += (bandHigher - bandBasic) * 0.40;
            totalTaxDue += bandBasic * 0.20;
        } else if (taxablePay > bandBasic) {
            totalTaxDue += (taxablePay - bandBasic) * 0.40;
            totalTaxDue += bandBasic * 0.20;
        } else {
            totalTaxDue += taxablePay * 0.20;
        }
    }

    // Apply Regulatory Cap (50% of Gross) - Applied to the *period* logic
    if (totalTaxDue > periodGross * 0.5) {
        totalTaxDue = periodGross * 0.5;
    }

    let tax = totalTaxDue - taxPaidYTD;

    if (!cumulativeData) {
        tax = Math.max(0, tax);
    }

    // --- 4. National Insurance (Weekly/Monthly Period) ---
    // Employee NI
    let ni = 0;
    const niDivisor = frequency === 'weekly' ? 52 : 12;
    const periodNiThreshold = rates.ANNUAL_NI_PRIMARY / niDivisor;
    const periodNiUpper = rates.ANNUAL_NI_UPPER / niDivisor;

    if (grossPay > periodNiThreshold) {
        const niable = Math.max(0, Math.min(grossPay, periodNiUpper) - periodNiThreshold);
        const excess = Math.max(0, grossPay - periodNiUpper);
        ni = (niable * 0.08) + (excess * 0.02);
    }

    // Employer NI
    let employerNi = 0;
    const periodEmployerThreshold = rates.ANNUAL_NI_SECONDARY / niDivisor;
    if (grossPay > periodEmployerThreshold) {
        const niable = grossPay - periodEmployerThreshold;
        employerNi = niable * 0.138; // Standard rate
    }

    // --- 5. Pension (Always Calculated per Pay Period) ---
    let pension = 0;
    const pensionDivisor = frequency === 'weekly' ? 52 : 12;
    const periodPensionTrigger = rates.ANNUAL_PENSION_TRIGGER / pensionDivisor;
    const periodPensionLower = rates.ANNUAL_PENSION_LOWER / pensionDivisor;
    const periodPensionUpper = rates.ANNUAL_PENSION_UPPER / pensionDivisor;

    if (grossPay > periodPensionTrigger) {
        const qualifying = Math.max(0, Math.min(grossPay, periodPensionUpper) - periodPensionLower);
        pension = qualifying * 0.05;
    }

    return {
        gross: totalGrossForPeriod,
        tax: tax,
        ni: Math.max(0, ni),
        employerNi: Math.max(0, employerNi),
        pension: Math.max(0, pension),
        sspAmount: sspAmount > 0 ? sspAmount : undefined,
        net: totalGrossForPeriod - tax - ni - pension,
        taxWeek: cumulativeData ? cumulativeData.weekNumber : undefined,
        taxMethod
    };
};
